import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { PlayerService, SortConfig, TableSortingService } from '../../../services';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { Player } from '../../../models';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FilteringService } from '../../../services/shared/filtering/filtering.service';
import { SortableHeaderComponent } from '../../shared/sortable-header/sortable-header.component';
import { TablePaginationService, PaginationResult } from '../../../services/shared/table-pagination/table-pagination.service';
import { TablePaginationComponent, PageChangeEvent } from '../../shared/table-pagination/table-pagination.component';

@Component({
  selector: 'app-player-list',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, SortableHeaderComponent, TablePaginationComponent],
  templateUrl: './player-list.component.html',
  styleUrl: './player-list.component.scss',
})
export class PlayerListComponent implements OnInit {
  private loggingService = inject(LoggingService);
  private tableSortingService = inject(TableSortingService);
  private tablePaginationService = inject(TablePaginationService);

  // Signals for reactive state management
  searchText = signal<string>('');
  players = signal<Player[]>([]);
  sortConfig = signal<SortConfig | null>(null);
  currentPage = signal<number>(1);
  pageSize = signal<number>(20);

  // Computed values
  filteredPlayers = computed(() => {
    const allPlayers = this.players();
    const searchTerm = this.searchText();
    const sort = this.sortConfig();

    // First filter the players (always call the service, even with empty search)
    let filtered = this.filteringService.filterItems(
      allPlayers,
      searchTerm,
      ['playFirstName', 'playLastName', 'firstName', 'lastName', 'hometown', 'shortName', 'countryName', 'stateName', 'lpgaId', 'wwgrId']
    );

    // Then apply sorting if configured
    if (sort) {
      filtered = this.tableSortingService.sort(filtered, sort.key, sort.direction);
    }

    return filtered;
  });

  paginationResult = computed<PaginationResult<Player>>(() => {
    const filtered = this.filteredPlayers();
    const page = this.currentPage();
    const size = this.pageSize();

    return this.tablePaginationService.paginate(filtered, page, size);
  });

  constructor(
    private playerService: PlayerService,
    private filteringService: FilteringService
  ) { }

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.playerService.getAll()?.subscribe({
      next: (players: Player[]) => {
        this.players.set(players);
      },
      error: (error: any) => {
        this.loggingService.error('Error loading players:', error);
      },
    });
  }



  filterPlayers() {
    // Reset to first page when searching
    this.currentPage.set(1);
    // Trigger reactivity by updating searchText - computed will handle the rest
    this.filteredPlayers(); // This will cause recalculation
  }

  clearSearch() {
    this.searchText.set('');
    this.currentPage.set(1);
    this.filterPlayers();
  }

  onSort(event: { key: string; direction: 'asc' | 'desc' }) {
    this.sortConfig.set({ key: event.key, direction: event.direction });
  }

  onPageChange(event: PageChangeEvent) {
    this.currentPage.set(event.page);
  }
}
